"use client";

import { ChevronLeft, ChevronRight, RefreshCw, ShieldCheck } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Alert, Button, EmptyState } from "@nitro/ui";
import { apiFetch } from "../lib/api";

const DEFAULT_CATEGORY = "sports_car";
const PAGE_SIZE = 100;

export function IracingLicenseRanking({ initialData = null } = {}) {
  const [data, setData] = useState(initialData);
  const [activeCategory, setActiveCategory] = useState(initialData?.category?.key || DEFAULT_CATEGORY);
  const [page, setPage] = useState(initialData?.pagination?.page || 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialData ? "" : "No pudimos cargar el ranking iRacing. Intenta de nuevo.");
  const requestInFlight = useRef(false);
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const categories = useMemo(() => (
    Array.isArray(data?.categories) && data.categories.length
      ? data.categories
      : [{ key: DEFAULT_CATEGORY, label: "Sports Car", shortLabel: "Sports", count: rows.length }]
  ), [data, rows.length]);
  const pagination = data?.pagination || { page, limit: PAGE_SIZE, total: rows.length, totalPages: 1 };

  async function loadCategory(nextCategory, nextPage = 1) {
    if (requestInFlight.current) return;
    requestInFlight.current = true;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        category: nextCategory,
        page: String(nextPage),
        limit: String(PAGE_SIZE),
      });
      const nextData = await apiFetch(`/api/v1/iracing/licenses?${params.toString()}`, { timeoutMs: 10000 });
      if (!Array.isArray(nextData?.rows)) throw new Error("No pudimos leer los datos del ranking.");
      setData(nextData);
      setActiveCategory(nextData.category?.key || nextCategory);
      setPage(nextData.pagination?.page || nextPage);
    } catch (loadError) {
      setError(loadError.message || "No se pudo cargar el ranking iRacing.");
    } finally {
      requestInFlight.current = false;
      setLoading(false);
    }
  }

  return (
    <section className="simulator-hub-panel iracing-ranking-panel" id="ranking-iracing">
      <div className="simulator-hub-panel-head">
        <div>
          <p className="nitro-eyebrow">Ranking iRacing</p>
          <h2>Licencias de pilotos registrados</h2>
          <p>Datos publicos sincronizados desde iRacing para pilotos Nitro con cuenta iRacing registrada.</p>
        </div>
        <div className="iracing-ranking-summary" aria-label="Resumen del ranking">
          <ShieldCheck size={18} />
          <strong>{formatNumber(pagination.total)}</strong>
          <span>{data?.category?.label || "Sports Car"}</span>
        </div>
      </div>

      <div className="iracing-license-tabs" aria-label="Tipos de licencia iRacing">
        {categories.map((category) => (
          <button
            aria-pressed={category.key === activeCategory}
            className={category.key === activeCategory ? "is-active" : ""}
            disabled={loading}
            key={category.key}
            onClick={() => loadCategory(category.key, 1)}
            type="button"
          >
            <span>{category.shortLabel || category.label}</span>
            <strong>{formatNumber(category.count)}</strong>
          </button>
        ))}
      </div>

      {error ? <Alert tone="warning" title="No pudimos actualizar el ranking" actions={<Button iconStart={<RefreshCw size={16} />} loading={loading} variant="secondary" onClick={() => loadCategory(activeCategory, page)}>Reintentar</Button>}>{error}{data ? " Sigues viendo la última consulta correcta." : ""}</Alert> : null}

      <div className="iracing-ranking-table-wrap" aria-busy={loading}>
        <table className="iracing-ranking-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Nombre para mostrar</th>
              <th scope="col">País</th>
              <th scope="col">iRating</th>
              <th scope="col">Licencia</th>
              <th scope="col">Safety</th>
              <th scope="col">Actualizado</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.category}-${row.custId}`}>
                <td data-label="#">{row.position}</td>
                <td data-label="Nombre para mostrar">
                  <a href={row.pilotSlug ? `/pilotos/${row.pilotSlug}` : "#ranking-iracing"}>{row.displayName}</a>
                </td>
                <td data-label="Pais">
                  <strong>{row.country || "Pais pendiente"}</strong>
                  {row.city ? <span>{row.city}</span> : null}
                  {!row.city && row.countrySource === "iracing" ? <span>Bandera iRacing</span> : null}
                </td>
                <td data-label="iRating">{row.irating ? formatNumber(row.irating) : "-"}</td>
                <td data-label="Licencia"><em>{row.license || "Sin licencia"}</em></td>
                <td data-label="Safety">{row.safetyRating !== null && row.safetyRating !== undefined ? Number(row.safetyRating).toFixed(2) : "-"}</td>
                <td data-label="Actualizado">{formatDate(row.syncedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && !error && !loading ? <EmptyState title="No hay licencias sincronizadas" description="Las licencias aparecerán cuando existan datos públicos sincronizados para esta categoría." /> : null}
      </div>

      <footer className="iracing-ranking-footer">
        <span>
          Pagina {pagination.page || page} de {pagination.totalPages || 1}
          {loading ? " / actualizando..." : ""}
        </span>
        <div>
          <button
            disabled={loading || (pagination.page || page) <= 1}
            onClick={() => loadCategory(activeCategory, Math.max(1, (pagination.page || page) - 1))}
            type="button"
          >
            <ChevronLeft size={16} />
            Anterior
          </button>
          <button
            disabled={loading || (pagination.page || page) >= (pagination.totalPages || 1)}
            onClick={() => loadCategory(activeCategory, (pagination.page || page) + 1)}
            type="button"
          >
            Siguiente
            <ChevronRight size={16} />
          </button>
        </div>
      </footer>
    </section>
  );
}

function formatNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat("es-CO").format(number) : "0";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-CO", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    timeZone: "America/Bogota",
    year: "numeric",
  }).format(date);
}
